import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Div100vh from "react-div-100vh";
import { getDataB2B, updateFunctionB2B } from "@/lib/helper";
import Header from "@/b2b/components/Header";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";
import ProcessingScreen from "./processing";
import SearchableSelect from "@/components/common/Input/SearchableSelect";
import DateField from "@/components/common/Input/DateField";

export default function PropertyDetailsScreen({ theme }) {
  const navigate = useNavigate();
  const userIdentifier = localStorage.getItem("user_identifier");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const partner = localStorage.getItem("partnerIdentifier");

  const LOCK_IN_TENURES = {
    urbanroomz: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    flent:      [3, 4, 5, 6, 7, 8, 9, 10, 11],
    blossomstayz: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
     kots:         [3, 4, 5, 6, 7, 8, 9, 10, 11],
    housr:        [3, 4, 5, 6],
    oho:          [3, 4, 5, 6, 7, 8, 9, 10, 11],
  };

  const lockInOptions = LOCK_IN_TENURES[partner] || null;

  const [formData, setFormData] = useState({
    property_address: "",
    city: "",
    movein_date: "",
    tenure: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const [propData, setPropData] = useState(null);

  const getPropData = async () => {
    // Use the reliable partner tag (set by HomeScreen) — NOT theme.name, which
    // resolves asynchronously for DB-driven partners and would send `undefined`.
    if (!partner) return;
    try {
      const result = await getDataB2B(
        `b2b/onboard/get-properties/${partner}`,
        userIdentifier
      );
      setPropData(result);
    } catch (error) {
      console.error("Error fetching property data:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      // Here you would typically make an API call to submit the formData
      // await submitPropertyDetails(formData, userIdentifier);
      await updateFunctionB2B(
        formData,
        "b2b/onboard/update-lead",
        "Submit",
        "Add Property Details",
        userIdentifier
      );

      const partner = localStorage.getItem("partnerIdentifier");
      navigate(`/b/${userIdentifier}`);
      // Handle success response
    } catch (error) {
      console.error("Error submitting property details:", error);
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPropData();
  }, []);

  if (propData) {
    return (
      <Div100vh
        className={`flex flex-col justify-between px-8 py-8 gap-8 ${theme?.backgroundColor} ${theme?.textColor}`}
      >
        <div className="flex flex-col gap-12">
          <Header
            heading="Property Details"
            subheading="Please provide your property information"
          />
          <div className="flex flex-col gap-8">
            {/* Property Selection Dropdown */}
            <div className="flex flex-col">
              <label
                htmlFor="property-select"
                className={`text-sm mb-1 text-primary-500`}
              >
                Select Property
              </label>
              <SearchableSelect
                id="property-select"
                value={formData.property_address}
                onChange={(value) => {
                  const selectedProperty = propData.find(
                    (prop) => prop.address === value
                  );
                  handleInputChange(
                    "property_address",
                    selectedProperty.address
                  );
                  handleInputChange("city", selectedProperty?.city);
                }}
                options={propData.map((property) => ({
                  value: property.address,
                  label: `${property.label} - ${property.city}`,
                  keywords: property.address,
                }))}
                placeholder="Select your property"
                searchPlaceholder="Search by name, city, or address..."
                emptyMessage="No properties found."
                disabled={showErrorMessage}
              />
              {formData.property_address && (
                <p
                  className={`text-xs mt-2 ${
                    theme?.subHeadingColor || "text-gray-500"
                  }`}
                >
                  Address: {formData.property_address}
                </p>
              )}
            </div>

            {/* Move-in Date */}
            <DateField
              name="movein_date"
              label="Move-in Date"
              value={formData.movein_date}
              onChange={(value) => handleInputChange("movein_date", value)}
              required
              theme={theme}
            />

            {/* Lock in Period */}
            {lockInOptions && (
              <div className="relative flex flex-col h-14">
                <select
                  value={formData.tenure ? String(formData.tenure) : ""}
                  onChange={(e) =>
                    handleInputChange("tenure", Number(e.target.value))
                  }
                  disabled={showErrorMessage}
                  className={`border p-4 border-black focus:border-black focus:outline-none w-full h-full rounded-none ${
                    theme ? theme.backgroundColor : "bg-input-bg"
                  } ${theme?.textColor ? theme.textColor : "text-white"}`}
                >
                  <option value="" disabled className="text-gray-400">
                    Select Lock in Period
                  </option>
                  {lockInOptions.map((m) => (
                    <option key={m} value={String(m)}>
                      {m} {m === 1 ? "Month" : "Months"}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute left-3 text-xs -top-2 text-primary-500 px-1 ${
                    theme ? theme.backgroundColor : "bg-[#000]"
                  }`}
                >
                  Lock in Period
                </label>
              </div>
            )}

            {/* Monthly Rent */}
            {/* <TextField
              name="rent"
              label="Monthly Rent (₹)"
              value={formData.rent}
              onChange={(value) => handleInputChange("rent", value)}
              required
              type="number"
              inputMode="decimal"
              disabled={showErrorMessage}
            /> */}
          </div>
        </div>

        {showErrorMessage && (
          <p className="text-red-500 text-2xs mt-4">
            There was an error submitting your details. Please try again or
            contact our support team for assistance at{" "}
            <a
              href="tel:+919354235813"
              className="text-blue-500 underline hover:text-blue-700"
            >
              +919354235813
            </a>
            .
          </p>
        )}

        <PrimaryButton
          text="Submit"
          handleClick={handleSubmit}
          disabled={
            showErrorMessage ||
            !formData.property_address ||
            !formData.movein_date ||
            (lockInOptions && !formData.tenure)
          }
          loading={isLoading}
          className={`${theme?.primaryButtonBgColor} ${theme?.primaryButtonTextColor}`}
        />
      </Div100vh>
    );
  } else {
    return <ProcessingScreen theme={theme} recoverable />;
  }
}
